import React from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { Banner } from "../../components/mui";
import { fmtInputDate } from "../../design/domain";
import { useApp } from "../../design/AppContext";
import useDailyTaskReminder from "../../hook/useDailyTaskReminder";
import { MdWarningAmber } from "react-icons/md";

/**
 * 「今日尚未建立工項」全站橫幅。
 *
 * 掛在 Layout 的內容區最上方而不是各別頁面：RD 從哪一頁進系統都要看得到，
 * 一頁一頁加只會漏掉幾頁，而漏掉的那幾頁正好是他最常待的地方。
 *
 * 用常駐的 in-flow 橫幅而不是 Toast：Toast 是 fixed、2.6 秒就消失，
 * 剛好錯過或人不在座位上就等於沒提醒過，而這件事要提醒一整天。
 * 也因為是 in-flow，它只把內容往下推，不會蓋住任何按鈕。
 */
export default function DailyTaskBanner() {
  const { userInfo } = useApp();
  const navigate = useNavigate();
  const { missing, date } = useDailyTaskReminder(userInfo?.role);

  if (!missing) return null;

  /*
   * 營運日由後端釘在台北時間，使用者的瀏覽器卻可能在別的時區（出差、遠端），
   * 兩邊差一天時他會看到「今天」跟自己認知的今天對不上。
   * 不一樣就把日期寫出來，讓他知道橫幅在講哪一天 ——
   * 直接藏起來只會讓真的漏填的人永遠收不到提醒。
   */
  const dateNote = date && date !== fmtInputDate(new Date()) ? `（工作日 ${date}）` : "";

  /*
   * 外觀交給 Banner（MUI Alert，attention = error）：底色與字色由主題隨深淺色換值，
   * 原本寫死的 #b42318 / rgba(217,45,32,…) 在深色佈景下會變成一塊看不清楚的暗紅。
   *
   * 按鈕放在 Alert 的 action 位置；大字模式下文字會自己換行，不會把按鈕擠出去。
   * margin-bottom：橫幅是內容區的第一個子元素，與下方頁面內容之間要留距離。
   */
  return (
    <Banner
      kind="attention"
      role="alert"
      icon={<MdWarningAmber />}
      sx={{ mb: "var(--space-6)", alignItems: "center", flexWrap: "wrap", "& .MuiAlert-action": { pt: 0 } }}
      action={
        <Button
          type="button"
          variant="outlined"
          color="error"
          onClick={() => navigate("/MyTask")}
          /*
           * 導到個人工項，不是專案列表。
           *
           * 橫幅自己就寫了「在既有工項上更新進度也算」，而那件事只能在個人工項做；
           * 丟到專案列表等於叫他先挑一個專案、再自己找到那件工項，而他最可能要做的
           * 「在手上那件事上回報一句」反而繞了最遠。個人工項的清單底下也有
           * 「今天做了別的事，自己開一件」，真的要開新的也走得通。
           */
        >
          回報今日工作
        </Button>
      }
    >
      {/*
        文案要把「怎樣才算有交」講完整：判定包含「更新既有工項的進度」，
        只寫「開成工項」會讓每天推進度的人以為自己做的不算數，跑去多開一張空工項。
      */}
      <Box component="span">
        今日尚未回報日報{dateNote}。工項即為每日日報，請於下班前將今日工作新增為工項；
        更新既有工項的進度亦視為已回報。
      </Box>
    </Banner>
  );
}
